<template>
  <img
    :src="resolvedSrc"
    :srcset="resolvedSrcset || undefined"
    :sizes="resolvedSizes || undefined"
    :alt="alt"
    :width="width || undefined"
    :height="height || undefined"
    :loading="loading"
    :fetchpriority="fetchpriority || undefined"
    :decoding="decoding"
    class="optimized-img"
    v-bind="$attrs"
    :style="mergedStyle"
    @load="$emit('load', $event)"
  />
</template>

<script>
const DEFAULT_BREAKPOINTS = [320, 480, 768, 1024];

const CLOUDINARY_UPLOAD_SEGMENT = '/image/upload/';

const isCloudinaryUrl = (url = '') =>
  typeof url === 'string' &&
  url.includes('res.cloudinary.com') &&
  url.includes(CLOUDINARY_UPLOAD_SEGMENT);

export default {
  name: 'OptimizedImage',
  inheritAttrs: false,
  emits: ['load'],
  props: {
    src: {
      type: String,
      required: true,
    },
    alt: {
      type: String,
      default: '',
    },
    width: {
      type: [Number, String],
      default: null,
    },
    height: {
      type: [Number, String],
      default: null,
    },
    widths: {
      type: Array,
      default: () => [...DEFAULT_BREAKPOINTS],
    },
    /** Tope duro para el ancho máximo en srcset (Cloudinary) */
    maxWidth: {
      type: Number,
      default: 720,
    },
    sizes: {
      type: String,
      default: '',
    },
    loading: {
      type: String,
      default: 'lazy',
    },
    fetchpriority: {
      type: String,
      default: '',
    },
    decoding: {
      type: String,
      default: 'async',
    },
    /** Cloudinary: número (ej. 50), auto:low, auto:good, etc. */
    quality: {
      type: String,
      default: 'auto:low',
    },
    /** Evita dpr_auto (menos peso en pantallas 2x; un solo arte para dpr fijo) */
    skipDpr: {
      type: Boolean,
      default: false,
    },
    /** limit = dentro del bbox; scale = ancho exacto w_ */
    cloudinaryCrop: {
      type: String,
      default: 'limit',
      validator: (v) => ['limit', 'scale'].includes(v),
    },
    responsive: {
      type: Boolean,
      default: true,
    },
    /** object-fit para estabilizar layout con aspect-ratio */
    fit: {
      type: String,
      default: '',
    },
  },
  computed: {
    normalizedWidths() {
      const cap = Number.isFinite(this.maxWidth) && this.maxWidth > 0 ? this.maxWidth : 720;
      return [...new Set((this.widths || DEFAULT_BREAKPOINTS).map(Number))]
        .filter((w) => Number.isFinite(w) && w > 0 && w <= cap)
        .sort((a, b) => a - b);
    },
    intrinsicAspectStyle() {
      const w = Number(this.width);
      const h = Number(this.height);
      if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
        return {};
      }
      return {
        aspectRatio: `${w} / ${h}`,
      };
    },
    fitStyle() {
      if (!this.fit) return {};
      return { objectFit: this.fit };
    },
    mergedStyle() {
      return { ...this.intrinsicAspectStyle, ...this.fitStyle };
    },
    resolvedSrc() {
      if (!isCloudinaryUrl(this.src)) {
        return this.src;
      }

      const list = this.normalizedWidths;
      const fallbackWidth = list.length ? list[list.length - 1] : this.maxWidth;
      return this.buildCloudinaryUrl(this.src, fallbackWidth);
    },
    resolvedSrcset() {
      if (!this.responsive || !isCloudinaryUrl(this.src) || this.normalizedWidths.length === 0) {
        return '';
      }

      return this.normalizedWidths
        .map((width) => `${this.buildCloudinaryUrl(this.src, width)} ${width}w`)
        .join(', ');
    },
    resolvedSizes() {
      if (!this.responsive || !isCloudinaryUrl(this.src)) {
        return this.sizes;
      }

      return this.sizes || '100vw';
    },
  },
  methods: {
    buildCloudinaryUrl(url, width) {
      if (!isCloudinaryUrl(url)) {
        return url;
      }

      const [baseUrl, queryString] = url.split('?');
      const [beforeUpload, afterUpload] = baseUrl.split(CLOUDINARY_UPLOAD_SEGMENT);

      if (!afterUpload) {
        return url;
      }

      const qVal = String(this.quality || 'auto:low').trim();
      const transformations = ['f_auto', `q_${qVal}`];
      if (!this.skipDpr) {
        transformations.push('dpr_auto');
      }
      transformations.push(this.cloudinaryCrop === 'scale' ? 'c_scale' : 'c_limit');

      if (width) {
        transformations.push(`w_${Math.round(width)}`);
      }

      const optimizedUrl = `${beforeUpload}${CLOUDINARY_UPLOAD_SEGMENT}${transformations.join(',')}/${afterUpload.replace(/^\/+/, '')}`;

      return queryString ? `${optimizedUrl}?${queryString}` : optimizedUrl;
    },
  },
};
</script>

<style scoped>
.optimized-img {
  max-width: 100%;
  height: auto;
  vertical-align: middle;
}
</style>
